import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import SelectWithSearch from "../ui/SelectWithSearch";
//import { languages } from '../../utils/languages';
import { googleSupportedLanguages } from '../../services/languageService';
import { Checkbox } from '../ui/checkbox';
import { detectLanguage } from '../../services/evaluationService';

const CSVColumnSelector = ({ 
  isOpen, 
  onClose, 
  onSave, 
  columns,
  fileName,
  onSourceLanguageChange,
  onTargetLanguageChange,
  onNoHeadersChange,
  csvData,
  setIsSourceAutoDetected,
  setIsTargetAutoDetected,
  isMetricsMode = false
}) => {
  const [sourceColumn, setSourceColumn] = useState('');
  const [targetColumn, setTargetColumn] = useState('');
  const [referenceColumn, setReferenceColumn] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSourceLang, setSelectedSourceLang] = useState('');
  const [selectedTargetLang, setSelectedTargetLang] = useState('');
  const [noHeaders, setNoHeaders] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleNext = () => {
    const isValid = isMetricsMode 
      ? (sourceColumn && targetColumn && referenceColumn)
      : (sourceColumn && targetColumn);
    
    if (isValid) {
      setCurrentStep(2);
    }
  };

  const handleSave = () => {
    if (isMetricsMode) {
      // For metrics mode, we don't need language selection
      if (sourceColumn && targetColumn && referenceColumn) {
        onSave({
          sourceColumn,
          targetColumn,
          referenceColumn,
          fileName,
          noHeaders
        });
        onClose();
        setCurrentStep(1);
      }
    } else {
      // For editor mode, we need language selection
      if (sourceColumn && targetColumn && selectedSourceLang && selectedTargetLang) {
        onSave({
          sourceColumn,
          targetColumn,
          fileName,
          sourceLanguage: selectedSourceLang,
          targetLanguage: selectedTargetLang,
          noHeaders
        });
        if (onSourceLanguageChange) {
          onSourceLanguageChange(selectedSourceLang);
          setIsSourceAutoDetected(true);
        }
        if (onTargetLanguageChange){
          onTargetLanguageChange(selectedTargetLang);
          setIsTargetAutoDetected(true);
        }
        onClose();
        setCurrentStep(1);
      }
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSourceColumn('');
    setTargetColumn('');
    setReferenceColumn('');
    setSelectedSourceLang('');
    setSelectedTargetLang('');
    setNoHeaders(false);
    onClose();
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleSourceLangChange = (lang) => {
    setSelectedSourceLang(lang);
    if (onSourceLanguageChange) onSourceLanguageChange(lang);
  };

  const handleTargetLangChange = (lang) => {
    setSelectedTargetLang(lang);
    if (onTargetLanguageChange) onTargetLanguageChange(lang);
  };

  const handleNoHeadersChange = (checked) => {
    setNoHeaders(checked);
    if (onNoHeadersChange) onNoHeadersChange(checked);
  };

  const handleSourceColumnChange = async (column) => {
    setSourceColumn(column);    
    if (!csvData?.data) return;
    
    const firstRow = csvData.data.find(row => row[columns.indexOf(column)]?.trim());
    if (firstRow) {
      const text = firstRow[columns.indexOf(column)];
      const detected = await detectLanguage(text.trim().substring(0, 10));
      if (detected) {
        const matchedLang = googleSupportedLanguages.find(lang => lang.code.toLowerCase() === detected.toLowerCase());
        if (matchedLang && onSourceLanguageChange) {
          setSelectedSourceLang(matchedLang.name);
        }
      }
    }
  };

  const handleTargetColumnChange = async (column) => {
    setTargetColumn(column);    
    if (!csvData?.data) return;
    
    const firstRow = csvData.data.find(row => row[columns.indexOf(column)]?.trim());
    if (firstRow) {
      const text = firstRow[columns.indexOf(column)];
      const detected = await detectLanguage(text.trim().substring(0, 10));
      if (detected) {
        const matchedLang = googleSupportedLanguages.find(lang => lang.code.toLowerCase() === detected.toLowerCase());
        if (matchedLang && onTargetLanguageChange) {
          setSelectedTargetLang(matchedLang.name);
        }
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="gap-0 sm:max-w-[425px]" onEscapeKeyDown={e => { if (isDropdownOpen) e.preventDefault(); else handleClose(); }}>
        <DialogHeader>
          <DialogTitle className="mb-2">
            {currentStep === 1 
              ? (isMetricsMode ? 'Select Source, Target and Reference Columns' : 'Select Source and Target Columns')
              : 'Select Source and Target Languages'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid">
          <div className="mb-2">
            <div className="text-sm text-gray-400 mb-6">File: {fileName}</div>
            <div className="w-full mb-4">
              <label className={"flex items-center w-full"}>
                <Checkbox
                  checked={noHeaders}
                  onCheckedChange={handleNoHeadersChange}
                  disabled={currentStep === 2}
                  className="border-gray-300 mr-2"
                  tabIndex={-1}
                />
                <span className={`text-gray-700 text-sm ${currentStep === 2 ? 'opacity-40' : ''}`}>File doesn't have headers in the first row</span>
              </label>
            </div>
          </div>
          {currentStep === 1 ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isMetricsMode ? 'Source Text' : 'Source Language Column'}
                </label>
                <SelectWithSearch
                    options={columns}
                    onChange={handleSourceColumnChange}
                    isStringArray
                    selectPlaceholder="Select source column"
                    value={sourceColumn}
                    onDropdownOpenChange={setIsDropdownOpen}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isMetricsMode ? 'Target Translation' : 'Target Language Column'}
                </label>
                <SelectWithSearch
                    options={columns}
                    onChange={handleTargetColumnChange}
                    isStringArray
                    selectPlaceholder="Select target column"
                    value={targetColumn}
                    onDropdownOpenChange={setIsDropdownOpen}
                />
              </div>
              {isMetricsMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference Translation
                  </label>
                  <SelectWithSearch
                      options={columns}
                      onChange={setReferenceColumn}
                      isStringArray
                      selectPlaceholder="Select reference column"
                      value={referenceColumn}
                      onDropdownOpenChange={setIsDropdownOpen}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source Text Column
                </label>
                <input
                  className="w-full rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm"
                  value={sourceColumn}
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Text Column
                </label>
                <input
                  className="w-full rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm"
                  value={targetColumn}
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source Language
                </label>
                <SelectWithSearch
                  isSearch
                  options={googleSupportedLanguages}
                  onChange={handleSourceLangChange}
                  selectPlaceholder="Select source language"
                  value={selectedSourceLang}
                  onDropdownOpenChange={setIsDropdownOpen}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Language
                </label>
                <SelectWithSearch
                  isSearch
                  options={googleSupportedLanguages}
                  onChange={handleTargetLangChange}
                  selectPlaceholder="Select target language"
                  value={selectedTargetLang}
                  onDropdownOpenChange={setIsDropdownOpen}
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-6 w-full">
          {!isMetricsMode && (
            <div className="flex flex-col w-full gap-2">
              <span className="text-base font-medium text-gray-500 whitespace-nowrap">Step {currentStep} of 2</span>
              <div className="flex-1">
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-1.5 bg-[#111827] rounded-full transition-all duration-300" style={{ width: currentStep === 1 ? '50%' : '100%' }} />
                </div>
              </div>
            </div>
          )}
          <div className={`flex items-center space-x-2 ${!isMetricsMode ? 'ml-2' : 'ml-auto'}`}>
            {currentStep === 1 && (
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            )}
            {currentStep === 1 ? (
              <Button 
                onClick={isMetricsMode ? handleSave : handleNext}
                disabled={isMetricsMode 
                  ? (!sourceColumn || !targetColumn || !referenceColumn)
                  : (!sourceColumn || !targetColumn)}
              >
                {isMetricsMode ? 'Apply' : 'Next'}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={!selectedSourceLang || !selectedTargetLang}
                >
                  Apply
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CSVColumnSelector; 